import { defineElement } from '@umbraco-ui/uui-base/lib/registration';
import { demandCustomElement } from '@umbraco-ui/uui-base/lib/utils';
import { css, LitElement, html } from 'lit';
import { query, property, queryAssignedElements } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import '@umbraco-ui/uui-button/lib';
import '@umbraco-ui/uui-popover-container/lib';
import '@umbraco-ui/uui-symbol-more/lib';

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _childElements, _hiddenElements, _hiddenElementsMap, _visibilityBreakpoints, _resizeObserver, _childResizeObservers, _breakPointCalculationInProgress, _isConnected, _UUIResponsiveContainerElement_instances, initialize_fn, onResize_fn, onSlotChange_fn, cleanup_fn, calculateBreakPoints_fn, updateCollapsibleItems_fn, _onItemClicked;
let UUIResponsiveContainerElement = class extends LitElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _UUIResponsiveContainerElement_instances);
    this.collapse = "end";
    // These store the component's internal state
    __privateAdd(this, _childElements, []);
    // All child elements
    __privateAdd(this, _hiddenElements, []);
    // Elements in the dropdown
    __privateAdd(this, _hiddenElementsMap, /* @__PURE__ */ new Map());
    __privateAdd(this, _visibilityBreakpoints, []);
    // Width thresholds for each item
    // ResizeObserver watches for size changes
    __privateAdd(this, _resizeObserver, new ResizeObserver(__privateMethod(this, _UUIResponsiveContainerElement_instances, onResize_fn).bind(this)));
    __privateAdd(this, _childResizeObservers, []);
    __privateAdd(this, _breakPointCalculationInProgress, false);
    __privateAdd(this, _isConnected, false);
    __privateAdd(this, _onItemClicked, (e) => {
      const clickedElement = e.currentTarget;
      const originalElement = __privateGet(this, _hiddenElementsMap).get(clickedElement);
      if (originalElement) {
        this._popoverContainerElement?.hidePopover();
        originalElement.click();
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    __privateSet(this, _isConnected, true);
    __privateMethod(this, _UUIResponsiveContainerElement_instances, initialize_fn).call(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    __privateSet(this, _isConnected, false);
    __privateGet(this, _resizeObserver).disconnect();
    __privateMethod(this, _UUIResponsiveContainerElement_instances, cleanup_fn).call(this);
  }
  render() {
    const moreButton = html`
      <uui-button
        popovertarget="popover-container"
        style="display: none"
        id="more-button"
        label="More"
        compact>
        <slot name="trigger-content">
          <uui-symbol-more></uui-symbol-more>
        </slot>
      </uui-button>
    `;
    return html`
      <div id="main">
        ${this.collapse === "start" ? moreButton : ""}
        <div id="items-container">
          <slot @slotchange=${__privateMethod(this, _UUIResponsiveContainerElement_instances, onSlotChange_fn)}></slot>
        </div>
        ${this.collapse === "end" ? moreButton : ""}
      </div>
      <uui-popover-container
        id="popover-container"
        popover
        placement=${this.collapse === "start" ? "bottom-start" : "bottom-end"}>
        <div id="dropdown-container">
          ${repeat(__privateGet(this, _hiddenElements), (el) => html`${el}`)}
        </div>
      </uui-popover-container>
    `;
  }
};
_childElements = new WeakMap();
_hiddenElements = new WeakMap();
_hiddenElementsMap = new WeakMap();
_visibilityBreakpoints = new WeakMap();
_resizeObserver = new WeakMap();
_childResizeObservers = new WeakMap();
_breakPointCalculationInProgress = new WeakMap();
_isConnected = new WeakMap();
_UUIResponsiveContainerElement_instances = new WeakSet();
initialize_fn = async function() {
  demandCustomElement(this, "uui-button");
  demandCustomElement(this, "uui-popover-container");
  demandCustomElement(this, "uui-symbol-more");
  await this.updateComplete;
  if (!__privateGet(this, _isConnected)) return;
  __privateGet(this, _resizeObserver).observe(this._mainElement);
  requestAnimationFrame(() => {
    if (!__privateGet(this, _isConnected)) return;
    __privateMethod(this, _UUIResponsiveContainerElement_instances, onSlotChange_fn).call(this);
  });
};
// This runs when the container size changes
onResize_fn = function(entries) {
  if (!__privateGet(this, _isConnected)) return;
  const newWidth = entries[0].contentBoxSize[0].inlineSize;
  __privateMethod(this, _UUIResponsiveContainerElement_instances, updateCollapsibleItems_fn).call(this, newWidth);
};
// This runs when children are added/removed
onSlotChange_fn = function() {
  if (!__privateGet(this, _isConnected)) return;
  __privateMethod(this, _UUIResponsiveContainerElement_instances, cleanup_fn).call(this);
  __privateSet(this, _childElements, this._slottedNodes ? [...this._slottedNodes] : []);
  __privateGet(this, _childElements).forEach((el) => {
    const observer = new ResizeObserver(
      __privateMethod(this, _UUIResponsiveContainerElement_instances, calculateBreakPoints_fn).bind(this)
    );
    observer.observe(el);
    __privateGet(this, _childResizeObservers).push(observer);
  });
  __privateMethod(this, _UUIResponsiveContainerElement_instances, calculateBreakPoints_fn).call(this);
};
cleanup_fn = function() {
  __privateGet(this, _childResizeObservers).forEach((observer) => observer.disconnect());
  __privateSet(this, _childResizeObservers, []);
  __privateSet(this, _visibilityBreakpoints, []);
  __privateGet(this, _hiddenElements).forEach((el) => {
    el.removeEventListener("click", __privateGet(this, _onItemClicked));
  });
  __privateSet(this, _hiddenElements, []);
  __privateGet(this, _hiddenElementsMap).clear();
};
calculateBreakPoints_fn = async function() {
  if (!__privateGet(this, _isConnected)) return;
  if (__privateGet(this, _breakPointCalculationInProgress)) return;
  __privateSet(this, _breakPointCalculationInProgress, true);
  await this.updateComplete;
  const gapCSSVar = Number.parseFloat(
    getComputedStyle(this).getPropertyValue("--uui-responsive-container-gap")
  );
  const gap = Number.isNaN(gapCSSVar) ? 8 : gapCSSVar;
  let totalWidth = 0;
  for (let i = 0; i < __privateGet(this, _childElements).length; i++) {
    __privateGet(this, _childElements)[i].style.display = "";
    totalWidth += __privateGet(this, _childElements)[i].offsetWidth;
    __privateGet(this, _visibilityBreakpoints)[i] = totalWidth;
    totalWidth += gap;
  }
  const tolerance = 2;
  this._mainElement.style.width = totalWidth - gap + tolerance + "px";
  __privateMethod(this, _UUIResponsiveContainerElement_instances, updateCollapsibleItems_fn).call(this, this._mainElement.offsetWidth);
  __privateSet(this, _breakPointCalculationInProgress, false);
};
// The main logic that shows/hides items
updateCollapsibleItems_fn = function(containerWidth) {
  const moreButtonWidth = this._moreButtonElement?.offsetWidth || 40;
  const availableWidth = containerWidth - moreButtonWidth;
  __privateGet(this, _hiddenElements).forEach((el) => {
    el.removeEventListener("click", __privateGet(this, _onItemClicked));
  });
  __privateSet(this, _hiddenElements, []);
  __privateGet(this, _hiddenElementsMap).clear();
  const len = __privateGet(this, _visibilityBreakpoints).length;
  if (this.collapse === "end") {
    for (let i = 0; i < len; i++) {
      const breakpoint = __privateGet(this, _visibilityBreakpoints)[i];
      const element = __privateGet(this, _childElements)[i];
      const widthToCheck = i === len - 1 ? containerWidth : availableWidth;
      if (breakpoint <= widthToCheck) {
        element.style.display = "";
      } else {
        element.style.display = "none";
        const clone = element.cloneNode(true);
        clone.style.display = "";
        clone.addEventListener("click", __privateGet(this, _onItemClicked));
        __privateGet(this, _hiddenElementsMap).set(clone, element);
        __privateGet(this, _hiddenElementsMap).set(element, clone);
        __privateGet(this, _hiddenElements).push(clone);
      }
    }
  } else {
    const totalWidth = __privateGet(this, _visibilityBreakpoints)[len - 1] || 0;
    for (let i = 0; i < len; i++) {
      const element = __privateGet(this, _childElements)[i];
      const widthFromEnd = totalWidth - (i > 0 ? __privateGet(this, _visibilityBreakpoints)[i - 1] : 0);
      const isFirstPotentiallyVisible = i === 0 || __privateGet(this, _childElements)[i - 1].style.display === "none";
      const widthToCheck = isFirstPotentiallyVisible && __privateGet(this, _hiddenElements).length === 0 ? containerWidth : availableWidth;
      if (widthFromEnd <= widthToCheck) {
        element.style.display = "";
      } else {
        element.style.display = "none";
        const clone = element.cloneNode(true);
        clone.style.display = "";
        clone.addEventListener("click", __privateGet(this, _onItemClicked));
        __privateGet(this, _hiddenElementsMap).set(clone, element);
        __privateGet(this, _hiddenElementsMap).set(element, clone);
        __privateGet(this, _hiddenElements).push(clone);
      }
    }
  }
  if (__privateGet(this, _hiddenElements).length === 0) {
    this._moreButtonElement.style.display = "none";
    this._popoverContainerElement?.hidePopover();
  } else {
    this._moreButtonElement.style.display = "";
  }
  this.requestUpdate();
};
_onItemClicked = new WeakMap();
UUIResponsiveContainerElement.styles = [
  css`
      :host {
        display: flex;
        min-width: 0;
      }

      #main {
        display: flex;
        overflow: hidden;
        align-items: center;
      }

      #items-container {
        display: flex;
        gap: var(--uui-responsive-container-gap, var(--uui-size-3,9px));
        overflow: hidden;
        align-items: center;
      }

      #more-button {
        --uui-button-background-color: transparent;
        --uui-button-background-color-hover: var(--uui-color-surface-alt,#f3f3f5);
        flex-shrink: 0;
      }

      :host([collapse='end']) #more-button,
      :host(:not([collapse])) #more-button {
        margin-left: var(--uui-responsive-container-gap, var(--uui-size-3,9px));
      }

      :host([collapse='start']) #more-button {
        margin-right: var(--uui-responsive-container-gap, var(--uui-size-3,9px));
      }

      #dropdown-container {
        display: flex;
        flex-direction: column;
        background-color: var(--uui-color-surface,#fff);
        border-radius: var(--uui-border-radius,3px);
        box-shadow: var(--uui-shadow-depth-3,0 10px 20px rgba(0,0,0,0.19) , 0 6px 6px rgba(0,0,0,0.23));
        overflow: hidden;
        padding: var(--uui-size-space-2,6px);
        gap: var(--uui-size-space-1,3px);
      }
    `
];
__decorateClass([
  query("#more-button")
], UUIResponsiveContainerElement.prototype, "_moreButtonElement", 2);
__decorateClass([
  query("#popover-container")
], UUIResponsiveContainerElement.prototype, "_popoverContainerElement", 2);
__decorateClass([
  query("#main")
], UUIResponsiveContainerElement.prototype, "_mainElement", 2);
__decorateClass([
  property({ type: String, reflect: true })
], UUIResponsiveContainerElement.prototype, "collapse", 2);
__decorateClass([
  queryAssignedElements({ flatten: true })
], UUIResponsiveContainerElement.prototype, "_slottedNodes", 2);
UUIResponsiveContainerElement = __decorateClass([
  defineElement("uui-responsive-container")
], UUIResponsiveContainerElement);

export { UUIResponsiveContainerElement };
